import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useTranslation } from '@/i18n';
import { displayName } from '@/utils/helpers';
import type { AuthUser } from '@/types/api';

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  user: AuthUser | null | undefined;
  onProfileClick: () => void;
  onChangePasswordClick: () => void;
  onLogoutClick: () => void;
}

export function ProfileMenu({
  anchorEl,
  open,
  onClose,
  user,
  onProfileClick,
  onChangePasswordClick,
  onLogoutClick,
}: ProfileMenuProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const name = displayName(user);
  const initial = (name || 'U').slice(0, 1).toUpperCase();

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 300 },
            maxWidth: 320,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
            mt: 1.5,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 12px 32px rgba(0, 0, 0, 0.45)'
                : '0 12px 32px rgba(20, 33, 61, 0.08)',
            '& .MuiList-root': {
              p: 0,
            },
          },
        },
      }}
    >
      {/* User Identity Header */}
      <Box
        sx={{
          p: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: (tTheme) =>
            tTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0, 184, 148, 0.05)',
          borderRadius: '6px',
          mb: 0.75,
        }}
      >
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: '#00B894',
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: '1.05rem',
            boxShadow: '0 2px 8px rgba(0, 184, 148, 0.25)',
            flexShrink: 0,
          }}
        >
          {initial}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.3,
            }}
            noWrap
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              fontSize: '0.78rem',
              lineHeight: 1.3,
              mt: 0.25,
            }}
            noWrap
          >
            {user?.email || '—'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 0.75 }} />

      {/* Menu Items */}
      <MenuItem
        onClick={onProfileClick}
        sx={{
          minHeight: 52,
          borderRadius: '6px',
          px: 1.75,
          py: 1.25,
          gap: 1.5,
          color: 'text.primary',
          fontWeight: 600,
          fontSize: '0.9rem',
          '&:hover': {
            bgcolor: (tTheme) => alpha(tTheme.palette.primary.main, 0.08),
            color: 'primary.main',
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
          <PersonOutlineOutlinedIcon fontSize="small" />
        </ListItemIcon>
        {t('auth.profile')}
      </MenuItem>

      <MenuItem
        onClick={onChangePasswordClick}
        sx={{
          minHeight: 52,
          borderRadius: '6px',
          px: 1.75,
          py: 1.25,
          gap: 1.5,
          color: 'text.primary',
          fontWeight: 600,
          fontSize: '0.9rem',
          '&:hover': {
            bgcolor: (tTheme) => alpha(tTheme.palette.primary.main, 0.08),
            color: 'primary.main',
            '& .MuiListItemIcon-root': {
              color: 'primary.main',
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
          <LockOutlinedIcon fontSize="small" />
        </ListItemIcon>
        {t('auth.changePassword')}
      </MenuItem>

      <MenuItem
        onClick={onLogoutClick}
        sx={{
          minHeight: 52,
          borderRadius: '6px',
          px: 1.75,
          py: 1.25,
          gap: 1.5,
          color: 'error.main',
          fontWeight: 600,
          fontSize: '0.9rem',
          '&:hover': {
            bgcolor: 'rgba(229, 57, 53, 0.08)',
            color: 'error.dark',
            '& .MuiListItemIcon-root': {
              color: 'error.dark',
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 28, color: 'error.main' }}>
          <LogoutOutlinedIcon fontSize="small" />
        </ListItemIcon>
        {t('auth.logout')}
      </MenuItem>
    </Menu>
  );
}
